import { useState, useEffect } from "react";
import { DateTime } from "luxon";
import { useAuth } from "/imports/state/AuthContext";
import api from "/imports/api/client";
import useFetch from "/imports/hooks/useFetch";
import { PAGE_SIZE } from "./trainerScheduleHelpers";

export default function useTrainerSchedule() {
  const { user } = useAuth();

  const [refreshKey, setRefreshKey] = useState(0);
  const [viewMode, setViewMode] = useState("list");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [range, setRange] = useState(null);
  const [rangeScope, setRangeScope] = useState("all");
  const [page, setPage] = useState(1);
  const [weekAnchor, setWeekAnchor] = useState(null);
  const [attendanceOverrides, setAttendanceOverrides] = useState({});
  const [noteOverrides, setNoteOverrides] = useState({});

  const isWeekMode = viewMode === "week";
  const isRangeMode = Boolean(range);
  const params = isWeekMode
    ? { view: "week", from: weekAnchor || undefined }
    : isRangeMode
      ? { from: range.from, to: range.to, scope: rangeScope, page, limit: PAGE_SIZE }
      : { page, limit: PAGE_SIZE };

  const { data, loading, error } = useFetch(
    () => api.getTrainerSchedule(params),
    [refreshKey, viewMode, weekAnchor, range?.from, range?.to, rangeScope, page]
  );

  // A new range or sub-filter always restarts at the first page.
  useEffect(() => {
    setPage(1);
  }, [range?.from, range?.to, rangeScope]);

  // A fresh fetch carries authoritative statuses and notes, so any local edits
  // we layered on the previous result are no longer needed.
  useEffect(() => {
    setAttendanceOverrides({});
    setNoteOverrides({});
  }, [refreshKey, viewMode, weekAnchor, range?.from, range?.to, rangeScope, page]);

  const firstName = data?.member?.firstName || user?.profile?.firstName || "";

  const rawDays = data?.days || [];
  const hasOverrides = Object.keys(attendanceOverrides).length > 0 || Object.keys(noteOverrides).length > 0;
  const days = hasOverrides
    ? rawDays.map((d) => ({
        ...d,
        bookings: d.bookings.map((b) => ({
          ...b,
          // Keyed check, not a truthy one: a cleared note is stored as null and
          // must still override the note the fetch returned.
          ...(b._id in noteOverrides ? { trainerNote: noteOverrides[b._id] } : {}),
          clients: (b.clients || []).map((c) =>
            attendanceOverrides[c.bookishClientId] ? { ...c, status: attendanceOverrides[c.bookishClientId] } : c
          )
        }))
      }))
    : rawDays;
  const total = data?.total || 0;
  const totalPages = data?.totalPages || 1;
  const activeScope = isRangeMode ? rangeScope : "today_tomorrow";

  const handleViewChange = (_, next) => {
    if (next) setViewMode(next);
  };

  const handleLoadRange = () => {
    const from = fromDate || toDate;
    const to = toDate || fromDate;
    if (!from) return;
    setRangeScope("all");
    setRange({ from: from.toFormat("yyyy-MM-dd"), to: to.toFormat("yyyy-MM-dd") });
  };

  const handleClearRange = () => {
    setFromDate(null);
    setToDate(null);
    setRange(null);
    setRangeScope("all");
  };

  const shiftWeek = (deltaDays) => {
    const base = data?.weekStart || weekAnchor || DateTime.now().toFormat("yyyy-MM-dd");
    setWeekAnchor(DateTime.fromISO(base, { zone: "UTC" }).plus({ days: deltaDays }).toFormat("yyyy-MM-dd"));
  };

  const weekLabel =
    data?.weekStart && data?.weekEnd
      ? `${DateTime.fromISO(data.weekStart, { zone: "UTC" }).toFormat("dd LLL")} – ${DateTime.fromISO(data.weekEnd, { zone: "UTC" }).toFormat("dd LLL yyyy")}`
      : "";

  return {
    data,
    loading,
    error,
    firstName,
    days,
    total,
    totalPages,
    viewMode,
    isWeekMode,
    isRangeMode,
    activeScope,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    rangeScope,
    setRangeScope,
    page,
    setPage,
    weekAnchor,
    setWeekAnchor,
    weekLabel,
    handleViewChange,
    handleLoadRange,
    handleClearRange,
    shiftWeek,
    refresh: () => setRefreshKey((n) => n + 1),
    applyAttendance: (updates) => setAttendanceOverrides((prev) => ({ ...prev, ...updates })),
    applyNote: (bookishId, note) => setNoteOverrides((prev) => ({ ...prev, [bookishId]: note }))
  };
}
